import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '../../store'

import { sortTaskStatuses } from './helpers'
import {
  createTaskStatus,
  deleteTaskStatus,
  getTaskStatuses,
  updateTaskStatus
} from '../../lib/api'
import { AddErrorAlert, AddSuccessAlert } from '../alert'

type TaskStatusState = {
  statuses: TaskStatus[]
  loaded: boolean
  loading: boolean
  error: null | any
}

const initialState: TaskStatusState = {
  statuses: [],
  loaded: false,
  loading: false,
  error: null
}

export const taskStatusSlice = createSlice({
  name: 'taskStatus',
  initialState,
  reducers: {
    startLoad: (state: TaskStatusState) => {
      state.loading = true
    },

    finishLoad: (
      state: TaskStatusState,
      action: PayloadAction<TaskStatus[]>
    ) => {
      state.statuses = sortTaskStatuses(action.payload)
      state.loaded = true
      state.loading = false
    },

    add: (state: TaskStatusState, action: PayloadAction<TaskStatus>) => {
      const added = [...state.statuses, action.payload]
      state.statuses = sortTaskStatuses(added)
    },

    remove: (state: TaskStatusState, action: PayloadAction<string>) => {
      const removed = state.statuses.filter(
        status => status.id !== action.payload
      )
      state.statuses = sortTaskStatuses(removed)
    },

    update: (state: TaskStatusState, action: PayloadAction<TaskStatus>) => {
      const newStatus = action.payload
      const replaced = state.statuses.map(status => {
        if (status.id === newStatus.id) {
          return newStatus
        }

        return status
      })
      state.statuses = sortTaskStatuses(replaced)
    },

    failed: (state: TaskStatusState, action: PayloadAction<any>) => {
      state.error = action.payload
      state.loading = false
      state.loaded = true
    },

    patch: (
      state: TaskStatusState,
      action: PayloadAction<Record<string, Partial<TaskStatusUpdate>>>
    ) => {
      const data = action.payload

      const updated = state.statuses.map(status => {
        return {
          ...status,
          ...(data[status.id] ?? {})
        }
      })

      state.statuses = sortTaskStatuses(updated)
    }
  }
})

const { startLoad, finishLoad, patch } = taskStatusSlice.actions
export const { add, remove, update, failed } = taskStatusSlice.actions

export const fetchStatus = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(startLoad())

    try {
      const statuses = await getTaskStatuses()
      const filtered = statuses.filter(obj => Boolean(obj.name))

      dispatch(finishLoad(filtered))
    } catch (error) {
      dispatch(failed(error))
    }
  }
}

export const createStatus = (status: TaskStatusUpdate) => {
  return async (dispatch: AppDispatch) => {
    const data: TaskStatusUpdate = { ...status }

    if (data.colour) {
      data.colour = data.colour.substring(1)
    }

    if (!data.base_status) {
      data.base_status = 'open'
    }

    // API sets these fields
    delete data.system

    const created = await createTaskStatus(data)
    dispatch(add(created))
  }
}

export const deleteStatus = (id: string) => {
  return async (dispatch: AppDispatch) => {
    await deleteTaskStatus(id)
    dispatch(remove(id))
  }
}

export const updateStatus = (data: TaskStatus) => {
  return async (dispatch: AppDispatch) => {
    const { id } = data

    const updated = await updateTaskStatus(id, data)
    dispatch(update(updated))
  }
}

export const patchStatus = (
  data: (Partial<TaskStatusUpdate> & { id: string })[]
) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { statuses } = selectTaskStatus(getState())

    try {
      const toUpdate: Record<string, TaskStatusUpdate> = {}

      data.forEach(item => {
        const { id, ...props } = item
        const current = statuses.find(status => status.id === id)

        if (!current) {
          return
        }

        // Check if any of the updates are new
        const hasUpdate = Object.entries(props).some(
          ([key, val]) => current[key] !== val
        )

        if (hasUpdate) {
          toUpdate[id] = { ...current, ...item }
        }
      })

      // Send updates to server
      Object.values(toUpdate).forEach(status => {
        // @ts-ignore
        const { id, ...statusUpdate } = status

        delete statusUpdate.system
        statusUpdate.colour = statusUpdate.colour?.toLocaleLowerCase() ?? null

        updateTaskStatus(id, statusUpdate)
      })

      dispatch(patch(toUpdate))

      dispatch(AddSuccessAlert('Changes saved.'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const selectTaskStatus = (state: RootState) => state.taskStatus

export default taskStatusSlice.reducer
